import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-theme.css';
// import SidebarLogo from '../../assets/SVGs/SidebarLogo';
import { useNavigate } from 'react-router-dom';
import { ymdToDate, dateToYmd } from '../../utils/dateInput';
// import { goBack } from '../../utils/utils';
import LoginSideImg from '../../assets/SVGs/LogInSideImage';
import {
  SignUpFormType,
} from '../../utils/utils';
import { useTheme } from '../../context/ThemeContext';
import { signUpApi, getSignupFieldErrors, confirmSignUpOtp, resendConfirmationCode } from '../../utils/apis';
import { guarantorLookupHint } from '../../utils/guarantorLookupHint';
import ContactBanner from '../shared/ContactBanner';


// Seconds to disable the "Resend" control after a code is sent, to throttle repeat sends.
const RESEND_COOLDOWN_SECONDS = 60;

const Signup: React.FC = () => {
  const [formData, setFormData] = useState<SignUpFormType>({
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    dateOfBirth: '',
    guarantorNumber: '',
    guarantorLastName: '',
    uniqueCustomerIdentifier: '',
  });
  const { theme } = useTheme();
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpValidated, setOtpValidated] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setFormData({
      firstName: '',
      lastName: '',
      userName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      dateOfBirth: '',
      guarantorNumber: '',
      guarantorLastName: '',
      uniqueCustomerIdentifier: '',
    });
    setOtpValidated(false);
    setTouched({});
    setSubmitAttempted(false);
    setIsOtpSent(false);
    setOtp('');
  }, []);

  // Landing on the OTP step means a code was just sent — start the resend cooldown.
  useEffect(() => {
    if (isOtpSent) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    }
  }, [isOtpSent]);

  // Tick the resend cooldown down to zero, one second at a time.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const fieldErrors = useMemo(
    () => getSignupFieldErrors(formData, theme),
    [formData, theme],
  );
  const isValidInput = Object.keys(fieldErrors).length === 0;

  // Advisory only — deliberately NOT part of fieldErrors/isValidInput. The shapes are measured, not
  // guaranteed, and blocking signup on a wrong guess would be worse than an unnecessary hint.
  const guarantorFinHint = guarantorLookupHint(formData.guarantorNumber);

  // Reveal a field's error only after the user leaves it (or tries to submit) — no red on first paint.
  const errorFor = (field: string): string | undefined =>
    touched[field] || submitAttempted ? fieldErrors[field] : undefined;

  const baseInput =
    'w-full bg-[white] text-[#29406B] border-1 font-medium px-4 py-3 rounded-[12px] ring-1 ring-inset';
  const inputCls = (field: string) =>
    `${baseInput} ${errorFor(field) ? 'ring-red-400' : 'ring-[#DCE1E8]'}`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!isValidInput) {
      setMessage({
        type: 'error',
        text: 'Please fix the highlighted fields below and try again.',
      });
      return;
    }
    setMessage(null);
    signUpApi(formData, theme)
      .then((response) => {
        if (response.success === true) {
          setIsOtpSent(true);
          setMessage({
            type: 'success',
            text: 'OTP sent successfully! Please verify your email with the OTP sent to your email address.',
          });
        } else {
          setMessage({
            type: 'error',
            text: 'Failed to create account. Please try again later.',
          });
        }
      })
      .catch((error: any) => {
        setMessage({
          type: 'error',
          text: error?.response?.data?.message || 'Failed to create account. Please try again later.',
        });
      });
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      confirmSignUpOtp(formData.userName, otp)
        .then((response : any) => {
          if (response.success === true) {
            setIsOtpSent(false);
            setMessage({
              type: 'success',
              text: 'Account created successfully!',
            });
            setOtpValidated(true);
            setTimeout(
              () =>
                navigate('/login', {
                  state: {
                    signUp: true,
                    date: Date.now(),
                  },
                }),
              2000
            );
          } else {
            setMessage({
              type: 'error',
              text: 'Failed to create account. Please try again later.',
            });
          }
        })
        .catch((error : any) => {
          setMessage({
            type: 'error',
            text: error?.response?.data?.message || 'Failed to create account. Please try again later.',
          });
        });
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to verify account. Please try again later. ',
      });
    }
  };

  const handleResendOtp = () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    resendConfirmationCode(formData.userName)
      .then(() => {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setMessage({
          type: 'success',
          text: 'A new code has been sent to your email address.',
        });
      })
      .catch(() => {
        // Always the generic line — never echo the backend message, so a differentiated response
        // can't turn this into a soft account-existence hint.
        setMessage({
          type: 'error',
          text: 'Could not resend the code. Please try again in a moment.',
        });
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  const FieldError: React.FC<{ field: string }> = ({ field }) => {
    const err = errorFor(field);
    return err ? <p className="mt-1 text-xs text-red-600">{err}</p> : null;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-1 md:flex-row">
        {/* Left Side: Form */}
        <div className="flex flex-col justify-start overflow-auto md:justify-center w-full md:w-[55%] bg-[#F2F5F9] p-6 lg:p-12">
          <div className="sm:px-[32px] md:px-[64px]">
            {/* <div className="flex justify-between items-center mb-8">
              <span
                className="text-gray-600 cursor-pointer text-2xl font-bold"
                onClick={goBack}
              >
                ←
              </span>
              <SidebarLogo />
            </div> */}

            <h2 className="text-left text-2xl font-semibold leading-9 text-[#29406B]">
              {isOtpSent ? 'Verify Your Account' : 'Create an Account'}
            </h2>
          </div>

          <div className="mt-6 sm:px-[32px] md:px-[64px]">
            {message && (
              <div
                className={`p-4 rounded-md mb-4 ${
                  message.type === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {message.text}
              </div>
            )}

            {!isOtpSent && !otpValidated ? (
              <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label
                      htmlFor="firstName"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      First Name*
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={inputCls('firstName')}
                    />
                    <FieldError field="firstName" />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="lastName"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Last Name*
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={inputCls('lastName')}
                    />
                    <FieldError field="lastName" />
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label
                      htmlFor="username"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Username*
                    </label>
                    <input
                      id="username"
                      name="userName"
                      type="text"
                      value={formData.userName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={inputCls('userName')}
                    />
                    <FieldError field="userName" />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="phoneNumber"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Phone Number*
                    </label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={inputCls('phoneNumber')}
                    />
                    <FieldError field="phoneNumber" />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="font-semibold text-base text-[#29406B]"
                  >
                    Email*
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    className={inputCls('email')}
                  />
                  <FieldError field="email" />
                </div>


                {/* Configurable fields based on theme */}
                {theme.showDobField && (
                  <div>
                    <label
                      htmlFor="dateOfBirth"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Date of Birth*
                    </label>
                    <DatePicker
                      id="dateOfBirth"
                      name="dateOfBirth"
                      selected={ymdToDate(formData.dateOfBirth)}
                      onChange={(d: Date | null) =>
                        setFormData((prev) => ({ ...prev, dateOfBirth: dateToYmd(d) }))
                      }
                      onBlur={() => setTouched((prev) => ({ ...prev, dateOfBirth: true }))}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="mm/dd/yyyy"
                      minDate={new Date(1900, 0, 1)}
                      maxDate={new Date()}
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      dropdownMode="select"
                      required
                      calendarClassName={theme.client === 'stluke' ? 'stluke-datepicker' : undefined}
                      popperClassName={theme.client === 'stluke' ? 'stluke-datepicker-popper' : undefined}
                      wrapperClassName="w-full"
                      className={inputCls('dateOfBirth')}
                    />
                    <FieldError field="dateOfBirth" />
                  </div>
                )}

                {theme.showSsnField && (
                  <div>
                    <label
                      htmlFor="uniqueCustomerIdentifier"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Last 4 digits of SSN*
                    </label>
                    <input
                      id="uniqueCustomerIdentifier"
                      name="uniqueCustomerIdentifier"
                      type="text"
                      inputMode="numeric"
                      value={formData.uniqueCustomerIdentifier}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      maxLength={4}
                      pattern="[0-9]{4}"
                      placeholder="1234"
                      className={inputCls('uniqueCustomerIdentifier')}
                    />
                    <FieldError field="uniqueCustomerIdentifier" />
                  </div>
                )}

                {theme.showGarantorNumberField && (
                  <div>
                    <label
                      htmlFor="guarantorNumber"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      {theme.guarantorFieldLabel ?? 'Guarantor Number'}*
                    </label>
                    <input
                      id="guarantorNumber"
                      name="guarantorNumber"
                      type="text"
                      value={formData.guarantorNumber}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      maxLength={100}
                      placeholder={theme.guarantorFieldPlaceholder ?? 'From your statement, e.g. GN00000123'}
                      className={inputCls('guarantorNumber')}
                    />
                    <FieldError field="guarantorNumber" />
                    {/* Same advisory as the lookup modal, and it matters MORE here: a FIN typed at
                        signup passes validation (only /\d/ is required), is persisted on the User
                        row, and then every login routes it to Cerner by guarantorNumber and 404s --
                        with no profile screen to correct it. That is the one irreversible dead end in
                        the flow. Gated on the same theme key so no other tenant sees it. */}
                    {theme.showEncounterFinHint && guarantorFinHint && (
                      <p className="mt-1 text-xs leading-relaxed text-amber-700" role="status" aria-live="polite">
                        {guarantorFinHint}
                      </p>
                    )}
                  </div>
                )}

                {theme.showGarantorLastNameField && (
                  <div>
                    <label
                      htmlFor="guarantorLastName"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Guarantor's Last Name*
                    </label>
                    <input
                      id="guarantorLastName"
                      name="guarantorLastName"
                      type="text"
                      value={formData.guarantorLastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      maxLength={100}
                      className={inputCls('guarantorLastName')}
                    />
                    <FieldError field="guarantorLastName" />
                  </div>
                )}

                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label
                      htmlFor="password"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Password*
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={inputCls('password')}
                    />
                    {errorFor('password') ? (
                      <p className="mt-1 text-xs text-red-600">{errorFor('password')}</p>
                    ) : (
                      <p className="mt-1 text-xs text-[#5b6b8c]">
                        At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character.
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="confirmPassword"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Confirm Password*
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      className={inputCls('confirmPassword')}
                    />
                    <FieldError field="confirmPassword" />
                  </div>
                </div>
                <button
                  type="submit"
                  className={`flex px-4 py-3 w-full justify-center rounded-md px-3 py-1.5 text-xl font-semibold text-[#FFFFFF] shadow-sm ${theme['button-bg']} ${isValidInput ? 'opacity-100' : 'opacity-70'}`}
                >
                  Create Account
                </button>

                <p className="mt-4 text-center text-lg font-semibold">
                  Already have an account?{' '}
                  <a
                    href="/login"
                    className={`text-lg text-${theme.color} underline font-semibold cursor-pointer`}
                  >
                    Sign in
                  </a>
                </p>
              </form>
            ) : (
              !otpValidated && (
                <form className="space-y-6" onSubmit={handleOtpSubmit}>
                  <div>
                    <label
                      htmlFor="otp"
                      className="font-semibold text-base text-[#29406B]"
                    >
                      Enter OTP*
                    </label>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      className="w-full bg-[white] text-[#29406B] border-1 font-medium px-4 py-3 rounded-[12px] ring-1 ring-inset ring-[#DCE1E8]"
                    />
                  </div>

                  <button
                    type="submit"
                    className="flex px-4 py-3 w-full justify-center rounded-md bg-[#08B4BD] px-3 py-1.5 text-xl font-semibold text-[#FFFFFF] shadow-sm"
                  >
                    Verify OTP
                  </button>

                  <p className="mt-4 text-center text-base font-semibold text-[#29406B]">
                    Didn&apos;t get the code?{' '}
                    {resendCooldown > 0 ? (
                      <span className="text-[#5b6b8c]">Resend in {resendCooldown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={isResending}
                        className={`text-${theme.color} underline font-semibold cursor-pointer ${isResending ? 'opacity-60' : ''}`}
                      >
                        {isResending ? 'Sending…' : 'Resend'}
                      </button>
                    )}
                  </p>
                </form>
              )
            )}
            <ContactBanner />
          </div>
        </div>

        {/* Right Side: Illustration */}
        <div className="hidden md:flex md:w-[45%] items-center justify-end bg-[#242E49] h-screen">
          <LoginSideImg />
        </div>
      </div>
    </div>
  );
};

export default Signup;
